import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2, Camera, Volume2, VolumeX, Keyboard, X, RotateCcw, CheckCircle2, AlertTriangle, MapPin, Truck, Music, Loader2 } from "lucide-react";
import { unlockScanSound } from "@/components/scanSound";

const MARBLE = "https://static.prod-images.emergentagent.com/jobs/5002feba-6f52-4717-97be-f5b988f96fe9/images/2d7beb1d079a9eb5148b145e4cb14a560b6d9700969984e958f1946a906ea726.jpeg";
const CARRIERS = [["NAPOLI", MapPin], ["GLS", Truck], ["TIKTOK", Music]];
const GOLD = "#B8912F";

const ScanRow = ({ s, index }) => (
  <div className={`flex items-center gap-3 px-4 py-3 backdrop-blur-sm rounded-xl border shadow-sm animate-fadeIn ${s.duplicate ? "bg-red-600 border-red-700 text-white" : "bg-white/80 border-[#E8DDBE]"}`} data-testid={`session-scan-${index}`}>
    {s.duplicate
      ? <AlertTriangle className="w-6 h-6 shrink-0 text-white" />
      : s.pending
      ? <Loader2 className="w-5 h-5 shrink-0 animate-spin" style={{ color: GOLD }} />
      : s.order_id
      ? <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
      : <AlertTriangle className="w-5 h-5 shrink-0 text-amber-500" />}
    <div className="flex-1 min-w-0">
      <div className={`font-semibold break-words ${s.duplicate ? "text-white" : "text-gray-900"}`}>{s.customer_name || (s.duplicate ? "Pacco doppio" : s.pending ? "Salvataggio…" : s.error ? "Errore" : "Nessun ordine collegato")}</div>
      <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs ${s.duplicate ? "text-red-50" : "text-gray-500"}`}>
        <span className="font-mono break-all">{s.code}</span>
        {s.tracking && s.tracking !== s.code && <span className="font-mono break-all" data-testid={`session-tracking-${index}`}>Track {s.tracking}</span>}
        <span className="text-[10px] font-bold tracking-wider px-1.5 rounded-full border" style={s.duplicate ? { color: "#fff", borderColor: "#fff" } : { color: GOLD, borderColor: GOLD }}>{s.carrier}</span>
        <span className={`tabular-nums ${s.duplicate ? "" : "text-gray-400"}`}>{s.time}</span>
        {s.error && <span className={s.duplicate ? "font-bold text-white" : "text-red-600"} data-testid={s.duplicate ? `duplicate-label-${index}` : undefined}>{s.error}</span>}
      </div>
    </div>
  </div>
);

export const ScanStation = ({ inputProps, carrier, setCarrier, isReady, scans, todayTotal, onReset, onCamera, soundEnabled, setSoundEnabled, pendingUnmatched, onConfirmUnmatched, manualOpen, setManualOpen, onManualSubmit }) => {
  const rootRef = useRef(null);
  const manualRef = useRef(null);
  const [manualCode, setManualCode] = useState("");
  const [full, setFull] = useState(false);
  const [focused, setFocused] = useState(false);
  const count = scans.filter((s) => !s.error).length;
  const [bigTotal, setBigTotal] = useState(false);
  const [flash, setFlash] = useState(false);
  const latest = scans[0];

  useEffect(() => {
    if (!latest?.duplicate) return;
    setBigTotal(false);
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1800);
    return () => clearTimeout(t);
  }, [latest]);

  useEffect(() => {
    const onChange = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFull = () => {
    if (full) {
      if (document.fullscreenElement) document.exitFullscreen?.();
      setFull(false);
    } else {
      setFull(true);
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
    setTimeout(() => inputProps.ref.current?.focus(), 100);
  };

  const active = focused && isReady;

  const station = (
    <div
      ref={rootRef}
      className={`${full ? "fixed inset-0 z-[2147483000] rounded-none" : "relative rounded-3xl min-h-[560px]"} overflow-hidden flex flex-col cursor-pointer select-none`}
      style={{ backgroundImage: `url(${MARBLE})`, backgroundSize: "cover", backgroundPosition: "center", boxShadow: `inset 0 0 0 3px ${GOLD}` }}
      onClick={() => { unlockScanSound(); inputProps.ref.current?.focus(); }}
      data-testid="scan-station"
    >
      <input
        {...inputProps}
        onFocus={() => setFocused(true)}
        onBlur={(e) => { setFocused(false); inputProps.onBlur?.(e); }}
        inputMode="none"
        autoComplete="off"
        className="absolute opacity-0 pointer-events-none w-px h-px"
        data-testid="scanner-input"
      />
      {flash && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-red-600 text-white pointer-events-none" data-testid="duplicate-alert">
          <AlertTriangle className="w-20 h-20 mb-3" />
          <div className="text-3xl font-black tracking-wide text-center px-4">PACCO GIÀ SCANSIONATO</div>
          <div className="mt-2 text-lg font-mono">{latest?.code}</div>
          {latest?.customer_name && <div className="mt-1 text-base">{latest.customer_name}</div>}
        </div>
      )}
      {pendingUnmatched && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-amber-500 text-white px-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="unmatched-prompt">
          <AlertTriangle className="w-16 h-16 mb-3" />
          <div className="text-2xl font-black">Etichetta non associata</div>
          <div className="mt-1 font-mono text-lg break-all">{pendingUnmatched}</div>
          <div className="mt-4 text-lg font-semibold">Salvo comunque il codice?</div>
          <div className="mt-5 flex gap-4">
            <button onClick={() => onConfirmUnmatched(true)} className="px-8 py-3 rounded-full bg-white text-amber-700 text-lg font-black shadow" data-testid="unmatched-yes-btn">SÌ</button>
            <button onClick={() => onConfirmUnmatched(false)} className="px-8 py-3 rounded-full border-2 border-white text-white text-lg font-black" data-testid="unmatched-no-btn">NO</button>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-2 p-3 sm:p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap gap-1 bg-white/70 backdrop-blur rounded-full p-1 border border-[#E8DDBE]">
          {CARRIERS.map(([c, Icon]) => (
            <button key={c} onClick={() => { setCarrier(c); inputProps.ref.current?.focus(); }}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-bold tracking-wider transition-colors ${carrier === c ? "text-white" : "text-gray-600 hover:text-gray-900"}`}
              style={carrier === c ? { background: GOLD } : {}} data-testid={`station-carrier-${c}`}>
              <Icon className="w-3.5 h-3.5" />{c}
            </button>
          ))}
        </div>
        <button onClick={toggleFull} className="shrink-0 p-2.5 rounded-full bg-white/70 backdrop-blur border border-[#E8DDBE] hover:bg-white transition-colors" data-testid="scan-fullscreen-btn" aria-label="Schermo intero">
          {full ? <Minimize2 className="w-5 h-5" style={{ color: GOLD }} /> : <Maximize2 className="w-5 h-5" style={{ color: GOLD }} />}
        </button>
      </div>

      <div className="flex flex-col items-center justify-center py-6">
        <div className={`flex items-center gap-2 text-sm sm:text-base font-black tracking-[0.15em] uppercase px-5 py-2 rounded-full transition-colors ${active ? "bg-emerald-600 text-white" : "bg-white/80 text-gray-500"}`} data-testid="scan-status">
          <span className={`w-2.5 h-2.5 rounded-full ${active ? "bg-white animate-pulse" : "bg-gray-400"}`} />
          {focused ? `Pronto · ${carrier}` : "Tocca qui per attivare"}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setBigTotal(true); }}
          className="mt-3 flex flex-col items-center group"
          data-testid="scan-today-total-btn"
          aria-label="Ingrandisci totale di oggi"
        >
          <span key={todayTotal} className="font-serif leading-none tabular-nums animate-fadeIn" style={{ fontSize: full ? "min(38vw, 320px)" : "clamp(120px, 30vw, 220px)", color: GOLD, textShadow: "0 2px 0 rgba(255,255,255,0.8)" }} data-testid="scan-today-total">
            {todayTotal}
          </span>
          <span className="text-sm sm:text-base font-bold tracking-[0.25em] uppercase text-gray-700">pacchi oggi</span>
          <span className="text-[11px] text-gray-500 flex items-center gap-1"><Maximize2 className="w-3 h-3" />tocca per ingrandire · tutti i dispositivi</span>
        </button>
        <div className="mt-2 text-sm text-gray-600" data-testid="scan-count">Questa sessione: <b>{count}</b></div>
      </div>
      {bigTotal && (
        <div
          className="fixed inset-0 z-[2147483600] flex flex-col items-center justify-center cursor-pointer"
          style={{ backgroundImage: `url(${MARBLE})`, backgroundSize: "cover" }}
          onClick={(e) => { e.stopPropagation(); setBigTotal(false); inputProps.ref.current?.focus(); }}
          data-testid="big-total-overlay"
        >
          <div className="font-serif leading-none tabular-nums" style={{ fontSize: "min(70vw, 70vh)", color: GOLD }}>{todayTotal}</div>
          <div className="text-2xl sm:text-4xl font-black tracking-[0.3em] uppercase text-gray-800">pacchi oggi</div>
          <div className="mt-4 text-sm text-gray-500">tocca per chiudere</div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto px-4 space-y-2 pb-3" data-testid="session-scan-list">
        {scans.length === 0
          ? <p className="text-center text-sm text-gray-500 pt-4">Spara il primo codice: qui compariranno gli ordini collegati</p>
          : scans.map((s, i) => <ScanRow key={s.key} s={s} index={i} />)}
      </div>

      {manualOpen && (
        <form
          className="flex gap-2 px-4 pb-3"
          onClick={(e) => e.stopPropagation()}
          onSubmit={(e) => { e.preventDefault(); if (manualCode.trim().length >= 5) { onManualSubmit(manualCode); setManualCode(""); manualRef.current?.focus(); } }}
          data-testid="manual-code-form"
        >
          <input
            ref={manualRef}
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value.toUpperCase())}
            placeholder="Scrivi il codice"
            autoFocus
            autoComplete="off"
            className="flex-1 min-w-0 px-4 py-3 rounded-full border-2 bg-white font-mono text-lg outline-none"
            style={{ borderColor: GOLD }}
            data-testid="manual-code-input"
          />
          <button type="submit" className="px-5 rounded-full text-white font-bold" style={{ background: GOLD }} data-testid="manual-code-submit">Invia</button>
        </form>
      )}

      <div className="flex flex-wrap items-center justify-center gap-2 p-4 border-t border-[#E8DDBE] bg-white/60 backdrop-blur" onClick={(e) => e.stopPropagation()}>
        <button onClick={onCamera} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-semibold" style={{ background: GOLD }} data-testid="open-camera-btn">
          <Camera className="w-4 h-4" />Fotocamera
        </button>
        <button onClick={() => setSoundEnabled(!soundEnabled)} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#E8DDBE] text-sm text-gray-700" data-testid="scan-sound-btn">
          {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}{soundEnabled ? "Audio" : "Muto"}
        </button>
        <button
          onClick={() => { const next = !manualOpen; setManualOpen(next); if (!next) setTimeout(() => inputProps.ref.current?.focus(), 50); }}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm ${manualOpen ? "text-white" : "bg-white border-[#E8DDBE] text-gray-700"}`}
          style={manualOpen ? { background: GOLD, borderColor: GOLD } : {}}
          data-testid="scan-manual-btn"
        >
          {manualOpen ? <X className="w-4 h-4" /> : <Keyboard className="w-4 h-4" />}{manualOpen ? "Chiudi" : "Scrivi codice"}
        </button>
        <button onClick={onReset} disabled={!scans.length} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#E8DDBE] text-sm text-gray-700 disabled:opacity-40" data-testid="scan-reset-btn">
          <RotateCcw className="w-4 h-4" />Azzera
        </button>
      </div>
    </div>
  );

  return full ? createPortal(station, document.body) : station;
};
